import { create, type MessageInitShape } from "@bufbuild/protobuf";
import { FieldMaskSchema, type FieldMask } from "@bufbuild/protobuf/wkt";
import { BulkExportCodesRequestSchema, CodeSchema, CodeService, ExportCodeMasksRequestSchema, ExportCodeRequestSchema, ListCodesRequestSchema, type AddCodeToBatchRequest, type Batch, type BatchSchema, type BatchTemplate, type BulkImportCodesRequest, type Code, type CodeDesign, type CreateStudioConfigRequest, type FileFormat, type StudioConfig } from "#gen/codes/v3/codes_pb.js";
import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { AssetState } from "#lib/api/bundles.js";
import { handleError } from "#lib/api/error.js";
import type { Filter } from "#lib/api/pagination.js";
import { downloadBlob } from "#lib/blob.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export interface CodeList {
	codes: Code[];
	pageInfo?: PaginationPageInfo;
}

interface StudioConfigList {
	configs: StudioConfig[];
	pageInfo?: PaginationPageInfo;
}

export class CodesClient {
	private client: Client<typeof CodeService>;
	private options: (() => ClientOptions) | undefined;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.codes || "/", CodeService, options);
		this.options = options!;
	}

	async createCode(code: Code): Promise<Code> {
		try {
			const res = await this.client.createCode({ code });
			return res.code!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async addCodeToBatch(req: AddCodeToBatchRequest): Promise<Code> {
		try {
			const res = await this.client.addCodeToBatch(req);
			return res.code!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateBatch(batch: MessageInitShape<typeof BatchSchema>, mask: FieldMask): Promise<Batch> {
		try {
			const res = await this.client.updateBatch({ batch, mask });
			return res.batch!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async bulkImportCodes(req: BulkImportCodesRequest): Promise<void> {
		try {
			await this.client.bulkImportCodes(req);
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateCode(code: Code, mask?: FieldMask, batchVersion?: string): Promise<Code> {
		try {
			const res = await this.client.updateCode({ code, mask, batchVersion });
			return res.code!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createStudioConfig(studioConfig: CreateStudioConfigRequest): Promise<StudioConfig> {
		try {
			const res = await this.client.createStudioConfig(studioConfig);
			return res.studioConfig!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listStudioConfigs(orgId: string, workspaceId: string | undefined, filter?: Filter, pagination?: PaginationArguments, options?: CallOptions): Promise<StudioConfigList> {
		try {
			const res = await this.client.listStudioConfigs(
				{
					orgId,
					workspaceId,
					pagination,
					...filter,
				},
				options,
			);
			return { configs: res.studioConfigs, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async getStudioConfig(id: string): Promise<StudioConfig> {
		try {
			const res = await this.client.getStudioConfig({ id });
			return res.studioConfig!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getBatchTemplate(id: string): Promise<BatchTemplate> {
		try {
			const res = await this.client.getBatchTemplate({ id });
			return res.template!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listCodesInBatch(orgId: string, workspaceId: string, batchId: string, batchVersion?: string, filter?: Filter, omitDefaultMetadata?: boolean, pagination?: PaginationArguments, options?: CallOptions): Promise<CodeList> {
		try {
			const res = await this.client.listCodes(
				{
					orgId,
					workspaceId,
					batchId,
					namePrefix: filter?.namePrefix,
					tags: filter?.tags,
					folderPath: filter?.folderPath,
					states: filter?.states,
					pagination,
					exactName: filter?.exactName,
					batchVersion,
					omitDefaultMetadata,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getCodeInBatch(id: string, batchId: string, batchVersion?: string, omitDefaultMetadata?: boolean): Promise<Code> {
		try {
			const res = await this.client.getCodeInBatch({ id, batchId, batchVersion, omitDefaultMetadata });
			return res.code!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getCode(id: string, omitDefaultMetadata?: boolean): Promise<Code> {
		try {
			const res = await this.client.getCode({ id, omitDefaultMetadata });
			return res.code!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getCodes(ids: string[]): Promise<Code[]> {
		try {
			const res = await this.client.getCodes({ ids });
			return res.codes!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async previewDesign(brandKitId: string, design?: CodeDesign): Promise<CodeDesign> {
		try {
			const res = await this.client.previewDesign({ brandKitId, design });
			return res.codeDesign!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveCode(id: string): Promise<Code> {
		try {
			const res = await this.client.updateCode({
				code: create(CodeSchema, {
					id: id,
					state: AssetState.ARCHIVED,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.code!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async restoreCode(id: string): Promise<Code> {
		try {
			const res = await this.client.updateCode({
				code: create(CodeSchema, {
					id: id,
					state: AssetState.ACTIVE,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.code!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async exportCode(id: string, format: FileFormat, options?: CallOptions): Promise<string> {
		try {
			const link = await this.client.exportCode(
				create(ExportCodeRequestSchema, {
					id: id,
					format: format,
				}),
				options,
			);
			return link.url;
		} catch (e) {
			throw handleError(e);
		}
	}

	async bulkExportCodes(format: FileFormat, recipients: string[], orgId: string, workspaceId: string, batchId: string, filter?: Filter, pagination?: PaginationArguments, options?: CallOptions): Promise<string> {
		try {
			const link = await this.client.bulkExportCodes(
				create(BulkExportCodesRequestSchema, {
					req: create(ListCodesRequestSchema, {
						orgId,
						workspaceId,
						batchId,
						namePrefix: filter?.namePrefix,
						tags: filter?.tags,
						folderPath: filter?.folderPath,
						pagination,
					}),
					format: format,
					emailRecipients: recipients,
				}),
				options,
			);
			return link.zipUrl;
		} catch (e) {
			throw handleError(e);
		}
	}

	async exportCodeMasks(format: FileFormat, orgId: string, workspaceId: string, batchId: string, widthInches?: number, options?: CallOptions): Promise<string> {
		try {
			const link = await this.client.exportCodeMasks(
				create(ExportCodeMasksRequestSchema, {
					req: create(ListCodesRequestSchema, {
						orgId,
						workspaceId,
						batchId,
					}),
					format: format,
					widthInches,
				}),
				options,
			);
			return link.zipUrl;
		} catch (e) {
			throw handleError(e);
		}
	}

	async downloadCode(url: string, fileType: string, filename: string = "flowcode"): Promise<void> {
		return fetch(url, {
			method: "GET",
			mode: "cors",
			headers: {
				"Content-Type": "application/json",
			},
		})
			.then(response => response.blob())
			.then(blob => {
				return new Promise((resolve, reject): void => {
					const reader = new FileReader();
					reader.onload = function () {
						if (typeof reader.result === "string") {
							downloadBlob(`${filename}.${fileType}`, reader.result);
							resolve();
						}
						reject();
					};
					reader.onerror = reject;
					reader.readAsDataURL(blob);
				});
			});
	}

	async downloadZip(s3url: string, filename: string = "flowcodes"): Promise<void> {
		return this.options?.().fetch!(s3url, {
			method: "GET",
			mode: "cors",
			headers: {
				"Content-Type": "application/zip",
			},
		})
			.then(response => response.blob())
			.then(blob => {
				return new Promise((resolve, reject): void => {
					const reader = new FileReader();
					reader.onload = function () {
						if (typeof reader.result === "string") {
							downloadBlob(`${filename}.zip`, reader.result);
							resolve();
						}
						reject();
					};
					reader.onerror = reject;
					reader.readAsDataURL(blob);
				});
			});
	}
}
